import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NotificationBell } from "@/components/NotificationBell";
import {
  LayoutDashboard,
  Users,
  Wrench,
  Boxes,
  Smartphone,
  Building2,
  UserCog,
  TrendingUp,
  CalendarDays,
  ScrollText,
  TriangleAlert,
  PackageSearch,
  Settings,
  CalendarClock,
  LogOut,
  Menu,
  X,
  ArrowLeft,
  Recycle,
  ShieldCheck,
  Receipt,
  Gauge,
  Target,
  Handshake,
  BookUser,
  Wallet,
  Star,
  Undo2,
  SlidersHorizontal,
  MessageCircle,
  GraduationCap,
  BarChart3,
  Globe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_LABELS } from "@/lib/rbac";
import { AppUpdateBanner } from "@/pages/AppUpdate";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";

const STUDIO_NAV = [
  { to: "/studio/customers", label: "Customers", icon: Users },
  { to: "/studio/central-sales", label: "Central Sales Jobs", icon: Handshake },
  { to: "/studio/orders", label: "Material Order", icon: PackageSearch },
  { to: "/studio/warranty", label: "Warranty Registration", icon: ShieldCheck },
  { to: "/studio/protection", label: "Protection Plan Registration", icon: ShieldCheck },
  { to: "/studio/inspection", label: "Vehicle Inspection Report", icon: ScrollText },
];

const NAV = [
  { to: "/studio-desk", label: "Studio Desk", icon: Building2, onlyRoles: ["network_admin", "accountant", "sales_manager_products", "crm"] },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, hideFor: ["content_manager"] },
  { to: "/customers", label: "Customers", icon: Users, perm: "customers:read" },
  { to: "/jobs", label: "Jobs", icon: Wrench, perm: "jobs:read" },
  { to: "/b2b", label: "B2B Pipeline", icon: Handshake, perm: "b2b:read" },
  { to: "/detailers", label: "Detailer List", icon: BookUser, onlyRoles: ["sales_manager_products"] },
  { to: "/whatsapp", label: "WhatsApp Inbox", icon: MessageCircle,
    onlyRoles: ["central_sales_team", "ceo", "network_admin"] },
  { to: "/train-bot", label: "Train Bot", icon: GraduationCap, onlyRoles: ["network_admin"] },
  { to: "/statistics", label: "Statistic Analysis", icon: BarChart3, onlyRoles: ["network_admin"] },
  { to: "/landing-admin", label: "Landing Page", icon: Globe, onlyRoles: ["network_admin"] },
  { to: "/sales-stats", label: "Sales Stats", icon: BarChart3,
    onlyRoles: ["network_admin", "ceo", "central_sales_team"] },
  { to: "/slot-chart", label: "Slot Chart", icon: CalendarClock,
    onlyRoles: ["network_admin", "ceo", "central_sales_team", "crm", "sales_manager_products", "studio_owner", "studio_manager"] },
  { to: "/scheduling", label: "Scheduling", icon: CalendarDays, perm: "scheduling:read" },
  { to: "/stock", label: "Stock", icon: Boxes, perm: "stock:read" },
  { to: "/orders", label: "Material Orders", icon: PackageSearch, perm: "orders:read" },
  { to: "/returns-report", label: "Returns Report", icon: Undo2, onlyRoles: ["network_admin", "accountant"] },
  { to: "/adjustments-log", label: "Adjustments Log", icon: SlidersHorizontal, onlyRoles: ["network_admin", "accountant"] },
  { to: "/serial-trace", label: "Serial Traceability", icon: PackageSearch, onlyRoles: ["network_admin", "accountant", "sales_manager_products"] },
  { to: "/dues", label: "Studio Dues", icon: Wallet, perm: "dues:read" },
  { to: "/alerts", label: "Alerts", icon: TriangleAlert, perm: "alerts:read" },
  { to: "/profit", label: "Profit", icon: TrendingUp, perm: "reports:read" },
  { to: "/team-performance", label: "Team Performance", icon: Gauge, perm: "performance:read" },
  { to: "/targets", label: "Targets", icon: Target, perm: "targets:write" },
  { to: "/monthly-targets", label: "Monthly Targets", icon: Target, perm: "targets:read" },
  { to: "/wastage", label: "Wastage", icon: Recycle, perm: "reports:read" },
  { to: "/warranties", label: "Warranties", icon: ShieldCheck, perm: "warranty:read" },
  { to: "/protection", label: "Protection Plans", icon: ShieldCheck, perm: "protection:read" },
  { to: "/feedback", label: "Customer Feedback", icon: Star, perm: "feedback:read" },
  { to: "/accounts", label: "HQ Accounts", icon: Receipt, perm: "finance:read" },
  { to: "/audit", label: "Audit", icon: ScrollText, perm: "audit:read" },
  { to: "/app-update", label: "Veloskin App", icon: Smartphone,
    hideFor: ["studio_owner", "studio_manager", "content_manager", "crm", "brand_consultant", "studio_investor", "installer"] },
  { to: "/studios", label: "Studios", icon: Building2, perm: "studios:write" },
  { to: "/users", label: "Users", icon: UserCog, perm: "users:write" },
  { to: "/configuration", label: "Configuration", icon: Settings, perm: "config:write" },
];

const ROLE_LABEL = ROLE_LABELS;

export const Shell = ({ children }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = user.role === "studio" ? STUDIO_NAV : NAV.filter(
    (n) => (!n.perm || can(user, n.perm)) && !(n.hideFor || []).includes(user.role)
      && (!n.onlyRoles || n.onlyRoles.includes(user.role))
  );

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  const hasTabBar = ["studio_owner", "studio_manager"].includes(user.role);

  return (
    <div className="min-h-screen bg-background">
      {open && (
        <div
          data-testid="sidebar-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-brand-ink/50 lg:hidden"
        />
      )}
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-brand-deep text-white transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 shrink-0 items-center border-b border-brand-line bg-white px-5">
          <BrandMark className="h-8" />
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
          {items.map(({ to, label, icon: Icon }) => {
            const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={`flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  active
                    ? "bg-crimson-500 text-white shadow-[0_6px_18px_-8px_rgba(245,24,91,0.9)]"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.8} />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className={`shrink-0 border-t border-white/10 p-4 ${hasTabBar ? "pb-24" : ""} lg:pb-4`}>
          <p data-testid="sidebar-user-name" className="truncate text-sm font-semibold text-white">
            {user.name}
          </p>
          <p className="text-xs text-white/60">{user.role_label || ROLE_LABEL[user.role]}</p>
          {(user.studio_name || user.is_network) && (
            <p data-testid="sidebar-studio-name" className="mt-1 text-xs font-medium text-crimson-400">
              {user.studio_name ||
                (user.role === "central_sales_team"
                  ? "Central Sales B2C · my leads only"
                  : "Central team · all studios")}
            </p>
          )}
          <Button
            data-testid="logout-btn"
            variant="outline"
            size="sm"
            className="mt-3 min-h-[44px] w-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
            onClick={onLogout}
          >
            <LogOut className="mr-2 h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-brand-line bg-white/85 px-4 backdrop-blur-xl lg:px-8">
          <button
            data-testid="menu-toggle"
            className="flex h-11 w-11 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          {loc.pathname !== "/" && (
            <button
              data-testid="global-back-btn"
              className="flex min-h-[44px] items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-brand-ink"
              onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          <div className="hidden text-sm text-slate-500 lg:block">
            {user.role === "central_sales_team"
              ? "My leads"
              : user.is_network
                ? "Network-wide view"
                : user.studio_name}
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <div className="truncate text-xs font-semibold text-brand sm:text-sm">
              VELOSKIN PPF Network
            </div>
          </div>
        </header>
        {user.backdate_mode && (
          <div
            data-testid="backdate-banner"
            className="sticky top-16 z-20 flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-100 px-4 py-2 text-amber-900 lg:px-8"
          >
            <span className="flex items-center gap-2 text-xs font-semibold sm:text-sm">
              <CalendarClock className="h-4 w-4 shrink-0" />
              Backdated entry mode is ON — new records use the historical date you pick, not today.
            </span>
            <Link
              to="/configuration"
              data-testid="backdate-banner-link"
              className="shrink-0 rounded bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600"
            >
              Manage
            </Link>
          </div>
        )}
        <main className={`fade-in overflow-x-hidden p-4 ${hasTabBar ? "pb-24" : "pb-8"} lg:p-8 lg:pb-8`}>
          <AppUpdateBanner />
          {children}
        </main>
      </div>
      {["studio_owner", "studio_manager"].includes(user.role) && (
        <nav
          data-testid="mobile-tabbar"
          className="fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t border-brand-line bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden"
        >
          {[
            { to: "/dashboard", label: "Home", icon: LayoutDashboard },
            { to: "/jobs", label: "Jobs", icon: Wrench },
            { to: "/customers", label: "Customers", icon: Users },
            { to: "/slot-chart", label: "Slots", icon: CalendarClock },
          ].map(({ to, label, icon: Icon }) => {
            const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={`tab-${label.toLowerCase()}`}
                className={`flex min-h-[60px] flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors ${
                  active ? "text-crimson-600" : "text-slate-500"
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={active ? 2.2 : 1.8} />
                {label}
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
};
